import { useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { JOURNEY, journeyIndex, journeyPathForRoute } from '../lib/format';
import { IconCheck, IconLock } from './NavIcons';

/** Step tabs for the active company — keeps the selected step scrolled into view. */
export default function JourneyTabs({ status, ratingLocked }: { status?: string | null; ratingLocked?: boolean }) {
  const current = journeyIndex(status);
  const location = useLocation();
  const scrollerRef = useRef<HTMLElement>(null);
  const activePath = journeyPathForRoute(location.pathname);

  useEffect(() => {
    const active = scrollerRef.current?.querySelector<HTMLElement>('a[aria-current="page"]');
    active?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, [location.pathname, status]);

  return (
    <nav ref={scrollerRef} aria-label="Company journey" className="mz-scroll-thin -mx-1 overflow-x-auto px-1 pb-2">
      <ol className="flex min-w-max gap-1.5">
        {JOURNEY.map((step, i) => {
          const done = i < current;
          const journeyHere = i === current;
          const locked = step.key === 'RATED' && ratingLocked;
          const isActive = activePath === step.to;
          return (
            <li key={step.key}>
              <NavLink
                to={step.to}
                end
                className={() =>
                  `flex items-center gap-2 rounded-xl px-2.5 py-1.5 transition-colors ${
                    isActive ? 'bg-[#d6006e] text-white shadow-[0_6px_16px_rgba(214,0,110,0.28)]' : 'bg-white text-[#6b0030] hover:bg-[#fdf2f8]'
                  }`
                }
                style={() => (isActive ? undefined : { border: `1px solid ${journeyHere ? '#f9a8d4' : done ? '#a7f3d0' : '#fce7f3'}` })}
                aria-current={isActive ? 'page' : undefined}
              >
                <>
                  <span
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-black ${
                      isActive ? 'bg-white text-[#d6006e]' : done ? 'bg-emerald-500 text-white' : journeyHere ? 'bg-[#d6006e] text-white' : 'bg-[#fdf2f8] text-[#c084a0]'
                    }`}
                  >
                    {done && !isActive ? <IconCheck className="h-3 w-3" /> : i + 1}
                  </span>
                  <span className="leading-tight">
                    <span className="block text-xs font-bold">{step.label}</span>
                    <span className={`block text-[9px] font-semibold uppercase tracking-wider ${isActive ? 'text-white/75' : done ? 'text-emerald-700' : 'text-[#c084a0]'}`}>
                      {isActive ? 'Viewing' : done ? 'Done' : journeyHere ? 'Next' : 'Upcoming'}
                    </span>
                  </span>
                  {locked && <IconLock className={`h-3.5 w-3.5 ${isActive ? 'text-white' : 'text-[#d6006e]'}`} />}
                </>
              </NavLink>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
